import { useState, useEffect, useRef, useMemo, useTransition, useCallback, Fragment } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Play,
    Forward,
    Heart,
    Share2,
    Bell,
    ArrowDownToLine,
    ArrowUpFromLine,
    ChevronDown,
    ChevronUp,
    ChevronRight,
    Check,
    ListFilter,
    Clock,
    Eye,
    EyeOff,
    Trash2,
    X,
    Download,
    Loader2,
    Crown,
    BookOpen,
    ListPlus,
    Plus,
} from 'lucide-react';
import DiscordIcon from '@/components/icons/DiscordIcon';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import ReactionPicker from '@/components/user/ReactionPicker';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';

import { ObraCoverImage } from '@/components/obra/ObraCoverImage';
import { getObraImageUrl } from '@/utils/image-utils';
import { useAuth } from '@/context/AuthContext';
import ChapterComments from '@/components/capitulo/ChapterComments';
import { api, unwrapApiResponse } from '@/utils/api';
import { addObraToHistory } from '@/utils/history';

/** API devolve `hasObra` (camelCase); legado no front usava `has_obra`. */
function listaContemObra(lista: Record<string, unknown> | null | undefined): boolean {
    if (!lista || typeof lista !== 'object') return false;
    return !!(lista.hasObra ?? lista.has_obra);
}

// Notificações Discord/WhatsApp ainda não implementadas no manhwa-api — stubs locais
const notificationChannelsApi = {
    listChannels: (_options?: any): Promise<any[]> => Promise.resolve([]),
    getDiscordAuthUrl: (_returnUrl: string, _options?: any): Promise<{ url: string }> => Promise.resolve({ url: '' }),
    requestWhatsAppLink: (_options?: any): Promise<any> => Promise.resolve(null),
    getWhatsAppPending: (_options?: any): Promise<{ pending: boolean }> => Promise.resolve({ pending: false }),
    listSubscriptions: (_obrId?: number, _options?: any): Promise<any[]> => Promise.resolve([]),
    subscribeObra: (_obrId: number, _canal: string, _options?: any): Promise<any> => Promise.resolve(null),
    unsubscribeObra: (_obrId: number, _canal: string, _options?: any): Promise<any> => Promise.resolve(null),
};
import { useOfflineChapters } from '@/hooks/useOfflineChapters';
import { chapterApiPayloadSupportsOfflineDownload, effectiveImagePageCountFromApiChapter } from '@/utils/offline-chapter-eligibility';

import { ObraHero } from '@/components/obra/ObraHero';
import { ObraChaptersSection } from '@/components/obra/ObraChaptersSection';
import FilaDownloadPanel from '@/components/capitulo/FilaDownloadPanel';
import DownloadNotification from '@/components/capitulo/DownloadNotification';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

export default function ObraPage() {
    const { id: obraId } = useParams<{ id: string }>();
    if (!obraId) return null; // Or show error
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const highlightCommentId = searchParams.get('comentario') ? parseInt(searchParams.get('comentario')!) : undefined;
    const { isAuthenticated, user, logout } = useAuth();
    const [downloadToast, setDownloadToast] = useState<string | null>(null);
    const [progressToast, setProgressToast] = useState<string | null>(null);
    const {
        chapters: offlineChapters,
        enqueueDownload,
        downloadQueue,
        downloadingChapterId,
        downloadingChapterName,
        downloadProgress,
        downloadPhase,
    } = useOfflineChapters({
        onChapterDownloaded: (chapterName) => {
            setDownloadToast(chapterName);
        },
    });
    const downloadedChapterIds = useMemo(
        () => new Set(offlineChapters.map((c) => c.chapterId)),
        [offlineChapters]
    );

    // States
    const [obra, setObra] = useState<any>(null);
    const [loadError, setLoadError] = useState<'auth' | 'vip' | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [chapters, setChapters] = useState<any[]>([]);
    const [chaptersAscending, setChaptersAscending] = useState(false);

    const [isLoadingChapters, setIsLoadingChapters] = useState(false);
    /** Volumes recolhidos (só para novel com volume). Chave = "Volume X" */
    const [collapsedVolumes, setCollapsedVolumes] = useState<Set<string>>(new Set());
    const [isLoadingAction, setIsLoadingAction] = useState(false);
    const [commentsCount, setCommentsCount] = useState(0);
    // Notificação Discord/WhatsApp (canais vinculados + inscrição nesta obra)
    const [notificationChannels, setNotificationChannels] = useState<{ canal: string; identificador: string }[]>([]);
    const [notificationSubscriptions, setNotificationSubscriptions] = useState<{ obr_id: number; canal: string }[]>([]);
    const [notificationLoading, setNotificationLoading] = useState(false);
    // Modal vincular Discord (OAuth — um clique, sem código)
    const [showLinkDiscordModal, setShowLinkDiscordModal] = useState(false);
    const [linkLoading, setLinkLoading] = useState(false);
    const [linkError, setLinkError] = useState('');
    // Modal vincular WhatsApp: wa.me — gerar link, usuário envia mensagem, polling até verificado
    const [showLinkWhatsAppModal, setShowLinkWhatsAppModal] = useState(false);
    const [linkWhatsAppData, setLinkWhatsAppData] = useState<{ codigo: string; whatsapp_link: string; mensagem_exemplo?: string } | null>(null);
    const [linkWhatsAppLoading, setLinkWhatsAppLoading] = useState(false);
    const [linkWhatsAppError, setLinkWhatsAppError] = useState('');
    const [linkWhatsAppPolling, setLinkWhatsAppPolling] = useState(false);
    const [notificationChannelsLoaded, setNotificationChannelsLoaded] = useState(false);

    // New Lists state
    const [showListModal, setShowListModal] = useState(false);
    const [userListas, setUserListas] = useState<any[]>([]);
    const [isLoadingListas, setIsLoadingListas] = useState(false);
    const [listaToast, setListaToast] = useState<string | null>(null);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const [chapterSelectMode, setChapterSelectMode] = useState(false);
    const [selectedChapterIds, setSelectedChapterIds] = useState<Set<number>>(new Set());
    const [multiDlWorking, setMultiDlWorking] = useState(false);

    const [donatePreset, setDonatePreset] = useState<'5' | '10' | '15' | 'custom' | null>(null);
    const [donateCustom, setDonateCustom] = useState('');
    const [donateLoading, setDonateLoading] = useState(false);

    const chaptersHeaderRef = useRef<HTMLDivElement>(null);
    const chaptersFooterRef = useRef<HTMLDivElement>(null);
    const chaptersSectionRef = useRef<HTMLDivElement>(null);
    const commentsSectionRef = useRef<HTMLElement>(null);
    const [scrollingUp, setScrollingUp] = useState(true);
    const lastScrollYRef = useRef(0);

    const scrollToChaptersTop = () => chaptersHeaderRef.current?.scrollIntoView({ behavior: 'smooth' });
    const scrollToChaptersBottom = () => chaptersFooterRef.current?.scrollIntoView({ behavior: 'smooth' });

    const resolveDonateAmount = (): number | null => {
        if (donatePreset === '5') return 5;
        if (donatePreset === '10') return 10;
        if (donatePreset === '15') return 15;
        if (donatePreset === 'custom') {
            const n = Number(donateCustom.replace(',', '.').trim());
            return Number.isFinite(n) && n > 0 ? n : null;
        }
        return null;
    };

    const handleStartDonation = async () => {
        if (!obra?.obr_id || donateLoading) return;
        const amount = resolveDonateAmount();
        if (amount == null) {
            alert('Escolha um valor de doação válido.');
            return;
        }
        setDonateLoading(true);
        try {
            const res = await api.post(`obras/${obra.obr_id}/doar`, { amount, source: 'web' }, { requiresAuth: true });
            const checkoutUrl: string = res?.data?.checkoutUrl ?? '';
            if (!checkoutUrl) throw new Error('Checkout não retornado.');
            window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
        } catch {
            alert('Não foi possível iniciar a doação.');
        } finally {
            setDonateLoading(false);
        }
    };


    // Mobile: esconder botões Subir/Descer ao rolar para baixo, mostrar ao rolar para cima
    useEffect(() => {
        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const y = window.scrollY;
                    if (Math.abs(y - lastScrollYRef.current) > 10) {
                        if (y > lastScrollYRef.current) setScrollingUp(false);
                        else if (y < lastScrollYRef.current) setScrollingUp(true);
                        lastScrollYRef.current = y;
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };
        lastScrollYRef.current = window.scrollY;
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Carregar obra — capítulos vêm embutidos no response
    useEffect(() => {
        const fetchObra = async () => {
            try {
                setIsLoading(true);
                setIsLoadingChapters(true);
                setLoadError(null);
                const { data } = await api.get(`obras/${obraId}`);
                setObra(data);

                setChapters(data.capitulos ?? []);

                const oid = Number(data?.obr_id ?? data?.id);
                if (Number.isFinite(oid) && oid > 0) {
                    addObraToHistory({
                        id: oid,
                        nome: data?.obr_nome ?? data?.nome,
                        imagem: data?.obr_imagem ?? data?.imagem,
                    });
                }


            } catch (error: any) {
                console.error('Erro ao carregar obra:', error);
                if (error?.status === 401 || error?.isAuthError) {
                    setLoadError('auth');
                } else if (error?.status === 403 || (error?.message && String(error.message).includes('VIP'))) {
                    setLoadError('vip');
                }
            } finally {
                setIsLoading(false);
                setIsLoadingChapters(false);
            }
        };

        if (obraId) {
            fetchObra();
        }
    }, [obraId]);

    // Status de lido: carregar em segundo plano (debounce para não chamar a cada chunk)
    useEffect(() => {
        if (!isAuthenticated || !user?.usr_id || chapters.length === 0) return;

        const applyReadStatus = async () => {
            try {
                const { data: readIds } = await api.get(`readings/user/${user.usr_id}/chapters?obr_id=${obraId}`);
                if (!Array.isArray(readIds) || readIds.length === 0) return;

                const readSet = new Set(readIds);
                setChapters((prev) => {
                    const readNumbers = prev
                        .filter((c: any) => readSet.has(c.cap_id))
                        .map((c: any) => parseFloat(c.cap_num ?? 0));
                    const maxReadNumber = Math.max(...readNumbers, 0);
                    return prev.map((c: any) => {
                        const cNum = parseFloat(c.cap_num ?? 0);
                        const isExplicitlyRead = readSet.has(c.cap_id);
                        const isVisuallyRead = isExplicitlyRead || (cNum < maxReadNumber);
                        return { ...c, lido: isVisuallyRead };
                    });
                });
            } catch (_) { /* ignore */ }
        };

        const t = setTimeout(applyReadStatus, 400);
        return () => clearTimeout(t);
    }, [obraId, isAuthenticated, user?.usr_id, chapters.length]);    // Scroll até os comentários se houver um comentário destacado na URL
    useEffect(() => {
        if (highlightCommentId && !isLoading) {
            const timer = setTimeout(() => {
                const el = commentsSectionRef.current;
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [highlightCommentId, isLoading]);

    useEffect(() => {
        if (!progressToast) return;
        const timer = setTimeout(() => setProgressToast(null), 2200);
        return () => clearTimeout(timer);
    }, [progressToast]);
    // Canais de notificação
    const loadNotificationData = async () => {
        if (!isAuthenticated || !obra?.obr_id) return;
        try {
            const [channels, subs] = await Promise.all([
                notificationChannelsApi.listChannels({ requiresAuth: true }),
                notificationChannelsApi.listSubscriptions(obra.obr_id, { requiresAuth: true }),
            ]);
            setNotificationChannels(Array.isArray(channels) ? channels : []);
            setNotificationSubscriptions(Array.isArray(subs) ? subs : []);
        } catch {
            setNotificationChannels([]);
            setNotificationSubscriptions([]);
        } finally {
            setNotificationChannelsLoaded(true);
        }
    };
    useEffect(() => {
        if (!isAuthenticated || !obra?.obr_id) return;
        loadNotificationData();
    }, [isAuthenticated, obra?.obr_id]);

    // Retorno do OAuth Discord
    useEffect(() => {
        if (!searchParams || !isAuthenticated || !obra?.obr_id) return;
        const notify = searchParams.get('notify');
        const ok = searchParams.get('ok');
        const error = searchParams.get('error');
        if (notify !== 'discord') return;
        const cleanUrl = () => {
            const url = new URL(window.location.href);
            url.searchParams.delete('notify');
            url.searchParams.delete('ok');
            url.searchParams.delete('error');
            window.history.replaceState({}, '', url.pathname + (url.search || ''));
        };
        if (ok === '1') {
            loadNotificationData().then(async () => {
                try {
                    await notificationChannelsApi.subscribeObra(obra.obr_id, 'DISCORD', { requiresAuth: true });
                    setNotificationSubscriptions((prev) => [...prev, { obr_id: obra.obr_id, canal: 'DISCORD' }]);
                } catch (_) { }
            });
            cleanUrl();
        } else if (error) {
            setLinkError(decodeURIComponent(error));
            setShowLinkDiscordModal(true);
            cleanUrl();
        }
    }, [searchParams, isAuthenticated, obra?.obr_id]);

    const discordLinked = notificationChannels.some((c: any) => c.canal === 'DISCORD');
    const discordSubscribed = notificationSubscriptions.some((s: any) => s.obr_id === obra?.obr_id && s.canal === 'DISCORD');
    const whatsappLinked = notificationChannels.some((c: any) => c.canal === 'WHATSAPP');
    const whatsappSubscribed = notificationSubscriptions.some((s: any) => s.obr_id === obra?.obr_id && s.canal === 'WHATSAPP');

    const handleToggleDiscordNotify = async () => {
        if (!isAuthenticated) return navigate('/entrar');
        if (!obra?.obr_id || notificationLoading) return;
        if (!notificationChannelsLoaded) return;
        if (!discordLinked) {
            setLinkError('');
            setShowLinkDiscordModal(true);
            return;
        }
        try {
            setNotificationLoading(true);
            if (discordSubscribed) {
                await notificationChannelsApi.unsubscribeObra(obra.obr_id, 'DISCORD', { requiresAuth: true });
                setNotificationSubscriptions((prev) => prev.filter((s: any) => !(s.obr_id === obra.obr_id && s.canal === 'DISCORD')));
            } else {
                await notificationChannelsApi.subscribeObra(obra.obr_id, 'DISCORD', { requiresAuth: true });
                setNotificationSubscriptions((prev) => [...prev, { obr_id: obra.obr_id, canal: 'DISCORD' }]);
            }
        } catch (e: any) {
            alert(e?.message || 'Erro ao atualizar notificação.');
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleVincularDiscordOAuth = async () => {
        setLinkError('');
        setLinkLoading(true);
        try {
            const returnUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';
            const data = await notificationChannelsApi.getDiscordAuthUrl(returnUrl, { requiresAuth: true }) as { url?: string };
            if (data?.url) {
                window.location.href = data.url;
                return;
            }
            setLinkError('Não foi possível iniciar vínculo com Discord.');
        } catch (e: any) {
            setLinkError(e?.message || 'Erro ao vincular Discord.');
        } finally {
            setLinkLoading(false);
        }
    };

    const handleToggleWhatsAppNotify = async () => {
        if (!isAuthenticated) return navigate('/entrar');
        if (!obra?.obr_id || notificationLoading) return;
        if (!notificationChannelsLoaded) return;
        if (!whatsappLinked) {
            setLinkWhatsAppError('');
            setLinkWhatsAppData(null);
            setLinkWhatsAppPolling(false);
            setShowLinkWhatsAppModal(true);
            return;
        }
        try {
            setNotificationLoading(true);
            if (whatsappSubscribed) {
                await notificationChannelsApi.unsubscribeObra(obra.obr_id, 'WHATSAPP', { requiresAuth: true });
                setNotificationSubscriptions((prev) => prev.filter((s: any) => !(s.obr_id === obra.obr_id && s.canal === 'WHATSAPP')));
            } else {
                await notificationChannelsApi.subscribeObra(obra.obr_id, 'WHATSAPP', { requiresAuth: true });
                setNotificationSubscriptions((prev) => [...prev, { obr_id: obra.obr_id, canal: 'WHATSAPP' }]);
            }
        } catch (e: any) {
            alert(e?.message || 'Erro ao atualizar notificação.');
        } finally {
            setNotificationLoading(false);
        }
    };

    const fetchUserListas = async (opts?: { silent?: boolean }) => {
        if (!isAuthenticated) return;
        if (!opts?.silent) setIsLoadingListas(true);
        try {
            const { data } = await api.get('listas', { params: { obra: obraId } });
            setUserListas(data || []);
        } catch (error) {
            console.error('Erro ao buscar listas do usuário:', error);
        } finally {
            if (!opts?.silent) setIsLoadingListas(false);
        }
    };

    const listasComObra = useMemo(
        () =>
            userListas
                .filter((l: Record<string, unknown>) => listaContemObra(l))
                .map((l: any) => ({ id: Number(l.id), nome: String(l.nome ?? 'Lista') }))
                .filter((l) => Number.isFinite(l.id) && l.id > 0),
        [userListas],
    );

    useEffect(() => {
        if (!obra || !isAuthenticated || !obraId) return;
        void fetchUserListas({ silent: true });
    }, [obra?.obr_id, isAuthenticated, obraId]);

    useEffect(() => {
        if (!listaToast) return;
        const t = setTimeout(() => setListaToast(null), 3500);
        return () => clearTimeout(t);
    }, [listaToast]);

    useEffect(() => {
        if (!showListModal) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowListModal(false);
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [showListModal]);

    const handleAddRemoveFromLista = async (listaId: number, listaNome: string, estavaNaLista: boolean) => {
        try {
            const res = await api.post(`listas/${listaId}/adicionar-remover-obra`, { obra: obraId }, { requiresAuth: true });
            const out = unwrapApiResponse<{ message?: string }>(res);
            await fetchUserListas({ silent: true });
            const msgApi = out?.message?.trim();
            setListaToast(
                msgApi
                    ? msgApi
                    : estavaNaLista
                      ? `Removida de «${listaNome}».`
                      : `Adicionada em «${listaNome}».`,
            );
        } catch (error) {
            console.error('Erro ao adicionar/remover da lista:', error);
            alert('Não foi possível atualizar a lista. Tente de novo.');
        }
    };

    const handleLinkWhatsAppGenerate = async () => {
        setLinkWhatsAppError('');
        setLinkWhatsAppLoading(true);
        try {
            const data = await notificationChannelsApi.requestWhatsAppLink({ requiresAuth: true }) as any;
            setLinkWhatsAppData({ codigo: data.codigo, whatsapp_link: data.whatsapp_link, mensagem_exemplo: data.mensagem_exemplo });
            if (data.whatsapp_link) window.open(data.whatsapp_link, '_blank');
            setLinkWhatsAppPolling(true);
        } catch (e: any) {
            setLinkWhatsAppError(e?.message || 'Não foi possível gerar o link.');
        } finally {
            setLinkWhatsAppLoading(false);
        }
    };

    useEffect(() => {
        if (!showLinkWhatsAppModal || !linkWhatsAppPolling || !linkWhatsAppData) return;
        const t = setInterval(async () => {
            try {
                const res = await notificationChannelsApi.getWhatsAppPending({ requiresAuth: true }) as { pending?: boolean; verified?: boolean };
                if (res.verified) {
                    setLinkWhatsAppPolling(false);
                    setLinkWhatsAppData(null);
                    setShowLinkWhatsAppModal(false);
                    await loadNotificationData();
                    if (obra?.obr_id) {
                        await notificationChannelsApi.subscribeObra(obra.obr_id, 'WHATSAPP', { requiresAuth: true });
                        setNotificationSubscriptions((prev) => [...prev, { obr_id: obra.obr_id, canal: 'WHATSAPP' }]);
                    }
                }
            } catch (_) { /* ignore */ }
        }, 2500);
        return () => clearInterval(t);
    }, [showLinkWhatsAppModal, linkWhatsAppPolling, linkWhatsAppData, obra?.obr_id, isAuthenticated]);

    const toggleOrder = () => {
        setChaptersAscending((prev) => !prev);
    };

    const isNovel = obra?.formato?.formt_id === 3 || obra?.formato?.formt_nome?.trim().toLowerCase() === 'novel';
    const hasVolumes = isNovel && chapters.some((c: any) => c.cap_volume != null && String(c.cap_volume).trim() !== '');

    const displayChapters = useMemo(() => {
        const num = (c: any) => parseFloat(c.cap_num ?? 0);
        const volKey = (c: any) => {
            const v = c.cap_volume != null ? String(c.cap_volume).trim() : '';
            if (v === '') return 0;
            const n = parseFloat(v);
            return Number.isNaN(n) ? 0 : n;
        };

        const asc = chaptersAscending;
        let sorted = [...chapters].sort((a: any, b: any) => {
            if (hasVolumes) {
                const vA = volKey(a), vB = volKey(b);
                if (vA !== vB) return asc ? vA - vB : vB - vA;
            }
            const numA = num(a), numB = num(b);
            return asc ? numA - numB : numB - numA;
        });

        return sorted;
    }, [chapters, chaptersAscending, hasVolumes]);

    const [showAllChapters, setShowAllChapters] = useState(false);
    const INITIAL_VISIBLE_CHAPTERS = 50;

    const visibleChapters = useMemo(() => {
        if (showAllChapters || displayChapters.length <= INITIAL_VISIBLE_CHAPTERS) {
            return displayChapters;
        }
        return displayChapters.slice(0, INITIAL_VISIBLE_CHAPTERS);
    }, [displayChapters, showAllChapters]);

    const chaptersByVolume = useMemo(() => {
        if (!hasVolumes || !visibleChapters.length) return null;
        const groups: { volume: string; chapters: any[] }[] = [];
        for (const c of visibleChapters) {
            const vol = (c.cap_volume != null && String(c.cap_volume).trim() !== '') ? String(c.cap_volume).trim() : '—';
            if (groups.length > 0 && groups[groups.length - 1].volume === vol) {
                groups[groups.length - 1].chapters.push(c);
            } else {
                groups.push({ volume: vol, chapters: [c] });
            }
        }
        return groups;
    }, [hasVolumes, visibleChapters]);

    const handleDownload = async (chapter: any, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const capId = Number(chapter?.cap_id ?? chapter?.id);
        if (!Number.isFinite(capId) || capId <= 0) {
            setProgressToast('Não foi possível identificar este capítulo para download.');
            return;
        }
        try {
            const { data } = await api.get(`capitulos/${capId}`);
            if (!chapterApiPayloadSupportsOfflineDownload(data)) {
                setProgressToast('Este capítulo não está disponível para download offline.');
                return;
            }
            const pageCount = Math.max(1, effectiveImagePageCountFromApiChapter(data));

            enqueueDownload({
                chapterId: capId,
                obraId: obra.obr_id,
                obraName: obra.obr_nome ?? '',
                obraImage: obra.obr_imagem ?? null,
                chapterNum: chapter.cap_num ?? '',
                chapterName: chapter.cap_nome ?? `Capítulo ${chapter.cap_num}`,
                pageCount,
                savedAt: Date.now(),
            });
            setProgressToast(`Cap. ${chapter.cap_num ?? capId} adicionado à fila de download.`);
        } catch (err) {
            console.error('Erro ao buscar dados para download:', err);
            setProgressToast('Falha ao iniciar download deste capítulo.');
        }
    };

    const handleDownloadAll = async () => {
        if (!obra || isDownloadingAll) return;

        const totalToFetch = totalChapters;
        const msg = totalToFetch > 50
            ? `Deseja baixar todos os ${totalToFetch} capítulos para leitura offline? Isso pode levar algum tempo e consumir bastante espaço.`
            : `Deseja baixar todos os ${totalToFetch} capítulos para leitura offline?`;

        if (!window.confirm(msg)) return;

        setIsDownloadingAll(true);
        try {
            // Capítulos já estão no estado (carregados junto com a obra)
            const allChapters = [...chapters].sort((a, b) => parseFloat(a.cap_num) - parseFloat(b.cap_num));

            if (allChapters.length === 0) {
                alert('Nenhum capítulo encontrado para baixar.');
                return;
            }

            // Filtrar o que já está baixado ou na fila
            const currentQueueIds = new Set(downloadQueue.map(q => q.chapterId));
            const toDownload = allChapters.filter((c: any) => {
                return !downloadedChapterIds.has(Number(c.cap_id)) && !currentQueueIds.has(Number(c.cap_id));
            });

            if (toDownload.length === 0) {
                alert('Todos os capítulos já foram baixados ou estão na fila.');
                return;
            }

            // 3. Enfileirar capítulos.
            // Para ser eficiente e não travar o worker do useOfflineChapters (que faz fetch manual),
            // vamos enfileirar com pageCount: 0 se não tivermos a info, e deixar o worker resolver.
            // Mas o useOfflineChapters worker faz o api.get(`capitulos/${id}`) de qualquer forma.
            // Então podemos enfileirar direto se soubermos que são "imagem".
            // Para garantir que são elegíveis, faremos o fetch em lotes de 10.

            const CHUNK_SIZE = 10;
            let countEnqueued = 0;

            for (let i = 0; i < toDownload.length; i += CHUNK_SIZE) {
                const chunk = toDownload.slice(i, i + CHUNK_SIZE);
                await Promise.all(chunk.map(async (chapter) => {
                    const capId = chapter.cap_id;
                    try {
                        const { data } = await api.get(`capitulos/${capId}`);
                        if (chapterApiPayloadSupportsOfflineDownload(data)) {
                            const pageCount = Math.max(1, effectiveImagePageCountFromApiChapter(data));
                            enqueueDownload({
                                chapterId: capId,
                                obraId: obra.obr_id,
                                obraName: obra.obr_nome ?? '',
                                obraImage: obra.obr_imagem ?? null,
                                chapterNum: chapter.cap_num ?? '',
                                chapterName: chapter.cap_nome ?? `Capítulo ${chapter.cap_num}`,
                                pageCount,
                                savedAt: Date.now(),
                            });
                            countEnqueued++;
                        }
                    } catch (err) {
                        console.error(`Erro ao processar capítulo ${capId} para download:`, err);
                    }
                }));
            }

            if (countEnqueued > 0) {
                alert(`${countEnqueued} capítulos foram adicionados à fila de download.`);
            } else {
                alert('Nenhum capítulo elegível para download foi encontrado.');
            }
        } catch (error) {
            console.error('Erro ao baixar tudo:', error);
            alert('Erro ao processar download em massa.');
        } finally {
            setIsDownloadingAll(false);
        }
    };

    const toggleChapterSelected = (capId: number) => {
        setSelectedChapterIds((prev) => {
            const next = new Set(prev);
            if (next.has(capId)) next.delete(capId);
            else next.add(capId);
            return next;
        });
    };

    const handleDownloadSelected = async () => {
        if (!obra || multiDlWorking) return;
        const caps = chapters.filter((c: any) => {
            const cid = Number(c.cap_id ?? c.id);
            if (!Number.isFinite(cid) || cid <= 0) return false;
            if (!selectedChapterIds.has(cid)) return false;
            if (downloadedChapterIds.has(cid)) return false;
            if (downloadQueue.some((q) => Number(q.chapterId) === cid)) return false;
            return true;
        });
        if (caps.length === 0) {
            setProgressToast('Nenhum capítulo selecionado elegível para download (já baixados ou na fila).');
            return;
        }
        setMultiDlWorking(true);
        try {
            const CHUNK_SIZE = 10;
            let countEnqueued = 0;
            for (let i = 0; i < caps.length; i += CHUNK_SIZE) {
                const chunk = caps.slice(i, i + CHUNK_SIZE);
                await Promise.all(
                    chunk.map(async (chapter: any) => {
                        const capId = Number(chapter.cap_id ?? chapter.id);
                        try {
                            const { data } = await api.get(`capitulos/${capId}`);
                            if (chapterApiPayloadSupportsOfflineDownload(data)) {
                                const pageCount = Math.max(1, effectiveImagePageCountFromApiChapter(data));
                                enqueueDownload({
                                    chapterId: capId,
                                    obraId: obra.obr_id,
                                    obraName: obra.obr_nome ?? '',
                                    obraImage: obra.obr_imagem ?? null,
                                    chapterNum: chapter.cap_num ?? '',
                                    chapterName: chapter.cap_nome ?? `Capítulo ${chapter.cap_num}`,
                                    pageCount,
                                    savedAt: Date.now(),
                                });
                                countEnqueued++;
                            }
                        } catch (err) {
                            console.error(`Erro ao processar capítulo ${capId} para download:`, err);
                        }
                    }),
                );
            }
            setProgressToast(
                countEnqueued > 0
                    ? `${countEnqueued} capítulo(s) adicionado(s) à fila de download.`
                    : 'Nenhum capítulo elegível para download entre os selecionados.',
            );
        } catch (e) {
            console.error(e);
            setProgressToast('Não foi possível preparar os downloads selecionados.');
        } finally {
            setMultiDlWorking(false);
            setChapterSelectMode(false);
            setSelectedChapterIds(new Set());
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-neutral-950 pb-20">
                <div className="absolute top-0 left-0 w-full h-[500px] overflow-hidden z-0">
                    <div className="absolute inset-0 bg-neutral-800/50 animate-pulse" />
                    <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/20 via-neutral-950/80 to-neutral-950" />
                </div>
                <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-24 lg:pt-32">
                    <div className="h-6 w-24 bg-white/10 rounded animate-pulse mb-8" />
                    <div className="md:hidden space-y-6">
                        <div className="flex justify-center">
                            <div className="w-[200px] aspect-[2/3] rounded-2xl bg-white/10 animate-pulse" />
                        </div>
                        <div className="space-y-3 text-center">
                            <div className="flex justify-center gap-2">
                                <div className="h-6 w-16 rounded-full bg-white/10 animate-pulse" />
                                <div className="h-6 w-20 rounded-full bg-white/10 animate-pulse" />
                            </div>
                            <div className="h-8 w-3/4 mx-auto bg-white/10 rounded animate-pulse" />
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    if (loadError === 'auth') {
        return (
            <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center text-white px-4 gap-6">
                <p className="text-center text-white/80">
                    Sua sessão expirou ou não tem permissão. Limpe a sessão e entre novamente.
                </p>
                <button
                    type="button"
                    onClick={() => {
                        logout();
                        window.location.href = '/entrar';
                    }}
                    className="px-6 py-3 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-xl transition-colors"
                >
                    Limpar sessão e entrar de novo
                </button>
            </div>
        );
    }

    if (loadError === 'vip') {
        return (
            <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center text-white px-4 py-8">
                <div className="w-full max-w-md flex flex-col items-center text-center gap-8">
                    <Crown className="w-12 h-12 text-amber-400" strokeWidth={1.5} />
                    <div className="space-y-3">
                        <h1 className="text-xl font-bold text-white">Conteúdo exclusivo para VIP</h1>
                        <p className="text-white/70 text-sm leading-relaxed">Esta obra só está disponível para assinantes.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <Link to="/landing" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-xl">
                            <Crown className="w-5 h-5" /> Ver planos VIP
                        </Link>
                        <button onClick={() => navigate(-1)} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white/15 text-white font-medium rounded-xl border border-white/10">
                            <ArrowLeft className="w-5 h-5" /> Voltar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!obra) {
        return <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-white">Obra não encontrada</div>;
    }

    const totalChapters = chapters.length;

    const markAllRead = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isAuthenticated) return navigate('/entrar');
        if (isLoadingAction || chapters.length === 0) return;
        const unread = chapters.filter((c: any) => !c.lido);
        if (unread.length === 0) return;
        try {
            setIsLoadingAction(true);
            await Promise.all(
                unread.map((c: any) =>
                    api.post(`readings/mark`, { obr_id: parseInt(obraId), cap_id: c.cap_id })
                )
            );
            setChapters((prev: any[]) => prev.map((c: any) => ({ ...c, lido: true })));
            setProgressToast(`${unread.length} capítulo(s) marcado(s) como lido.`);
        } catch (error) {
            console.error('Erro ao marcar todos:', error);
        } finally {
            setIsLoadingAction(false);
        }
    };

    const unmarkAllRead = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isAuthenticated || !user) return navigate('/entrar');
        if (isLoadingAction) return;
        const readChapters = chapters.filter((c: any) => c.lido);
        if (readChapters.length === 0) return;
        if (!confirm('Deseja desmarcar todos os capítulos lidos?')) return;
        try {
            setIsLoadingAction(true);
            await Promise.all(readChapters.map((c: any) => api.delete(`readings/unmark/${c.cap_id}`)));
            setChapters((prev: any[]) => prev.map((c: any) => ({ ...c, lido: false })));
        } catch (error) {
            console.error('Erro ao desmarcar todos:', error);
        } finally {
            setIsLoadingAction(false);
        }
    };

    const handleToggleChapterRead = async (chapterId: number, isRead: boolean, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isAuthenticated) return;
        const clickedChapter = chapters.find((c: any) => c.cap_id === chapterId);

        setChapters((prev: any[]) => {
            if (isRead) {
                return prev.map((c: any) =>
                    c.cap_id === chapterId ? { ...c, lido: false } : c
                );
            }
            const target = prev.find((c: any) => c.cap_id === chapterId);
            const targetNum = target ? parseFloat(target.cap_num ?? 0) : -1;
            return prev.map((c: any) => {
                const cNum = parseFloat(c.cap_num ?? 0);
                if (c.cap_id === chapterId || cNum < targetNum) return { ...c, lido: true };
                return c;
            });
        });

        try {
            if (isRead) {
                await api.delete(`readings/unmark/${chapterId}`);
                setProgressToast('Progresso removido.');
            } else {
                await api.post(`readings/mark`, { obr_id: parseInt(obraId), cap_id: chapterId });
                const label = clickedChapter?.cap_nome || clickedChapter?.cap_num || chapterId;
                setProgressToast(`Progresso salvo em ${label}.`);
            }
        } catch (error) {
            console.error('Erro ao marcar:', error);
            // Revert on error? Maybe not for now to keep it snappy
        }
    };

    return (
        <div className="min-h-screen bg-neutral-950 pb-20 overflow-x-hidden">
            <FilaDownloadPanel
                downloadingName={downloadingChapterName}
                downloadProgress={downloadProgress}
                downloadPhase={downloadPhase as any}
                fila={downloadQueue.map((q) => ({ chapterId: q.chapterId, chapterName: q.chapterName }))}
            />

            <DownloadNotification
                chapterName={downloadToast}
                duration={3000}
                onClose={() => setDownloadToast(null)}
            />
            {progressToast && (
                <div className="fixed top-20 right-4 z-[120] rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-200 shadow-xl backdrop-blur-md">
                    {progressToast}
                </div>
            )}
            {listaToast && (
                <div
                    className={`fixed right-4 z-[121] max-w-[min(90vw,22rem)] rounded-xl border border-orange-400/35 bg-orange-500/15 px-4 py-2.5 text-sm font-semibold text-orange-100 shadow-xl backdrop-blur-md ${progressToast ? 'top-32' : 'top-20'}`}
                >
                    {listaToast}
                </div>
            )}
            <ObraHero
                obra={obra}
                isAuthenticated={isAuthenticated}
                discordSubscribed={discordSubscribed}
                whatsappSubscribed={whatsappSubscribed}
                notificationLoading={notificationLoading}
                listasComObra={listasComObra}
                onToggleDiscordNotify={handleToggleDiscordNotify}
                onToggleWhatsAppNotify={handleToggleWhatsAppNotify}
                onShowListModal={() => {
                    if (!isAuthenticated) return navigate('/entrar');
                    fetchUserListas();
                    setShowListModal(true);
                }}
            />

            {obra && !loadError && Number.isFinite(Number(obraId)) && Number(obraId) > 0 && (
                <section className="mx-auto max-w-7xl px-4 pb-6 md:px-8 md:pb-8" aria-label="Reações à obra">
                    <ReactionPicker entityId={Number(obraId)} entityType="obra" variant="compact" />
                </section>
            )}

            {(obra?.mediocre === true || obra?.obr_mediocre === true) && (
                <section className="mx-auto max-w-7xl px-4 pb-8 md:px-8" aria-label="Doação para a obra">
                    <div className="max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                        <p className="text-sm font-semibold text-white/90">Doar para a obra</p>
                        <p className="mt-1 text-xs text-white/45">
                            O valor da doação é repassado aos responsáveis pelos capítulos.
                        </p>
                        <div className="mt-4 grid grid-cols-3 gap-2">
                            {(['5', '10', '15'] as const).map((val) => {
                                const active = donatePreset === val;
                                return (
                                    <button
                                        key={val}
                                        type="button"
                                        onClick={() => setDonatePreset(val)}
                                        className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${
                                            active
                                                ? 'border-orange-500 bg-orange-500/20 text-white'
                                                : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                                        }`}
                                    >
                                        R$ {val}
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            onClick={() => setDonatePreset('custom')}
                            className={`mt-2 h-11 w-full rounded-xl border text-sm font-semibold transition-colors ${
                                donatePreset === 'custom'
                                    ? 'border-orange-500 bg-orange-500/20 text-white'
                                    : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                            }`}
                        >
                            Personalizado
                        </button>
                        {donatePreset === 'custom' && (
                            <input
                                type="text"
                                inputMode="decimal"
                                value={donateCustom}
                                onChange={(e) => setDonateCustom(e.target.value)}
                                placeholder="Outro valor (R$)"
                                className="mt-2 h-11 w-full rounded-xl border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-orange-500/60"
                            />
                        )}
                        <button
                            type="button"
                            disabled={donateLoading || resolveDonateAmount() == null}
                            onClick={() => void handleStartDonation()}
                            className="mt-3 h-11 w-full rounded-xl border border-orange-500 bg-orange-500/20 text-sm font-semibold text-white transition-all hover:bg-orange-500/35 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {donateLoading ? 'Abrindo pagamento...' : 'Doar agora'}
                        </button>
                    </div>
                </section>
            )}

            <div ref={chaptersSectionRef}>
                <ObraChaptersSection
                    obra={obra}
                    chapters={visibleChapters}
                    isLoadingChapters={isLoadingChapters}
                    totalChapters={totalChapters}
                    chaptersAscending={chaptersAscending}
                    onToggleOrder={toggleOrder}
                    chaptersByVolume={chaptersByVolume}
                    collapsedVolumes={collapsedVolumes}
                    onToggleVolume={(vol) => {
                        setCollapsedVolumes(prev => {
                            const next = new Set(prev);
                            if (next.has(vol)) next.delete(vol);
                            else next.add(vol);
                            return next;
                        });
                    }}
                    isNovel={isNovel}
                    downloadedChapterIds={downloadedChapterIds}
                    onToggleRead={handleToggleChapterRead}
                    onDownload={handleDownload}
                    downloadingChapterId={downloadingChapterId}
                    downloadQueue={downloadQueue}
                    isAuthenticated={isAuthenticated}
                    onMarkAllRead={markAllRead}
                    onUnmarkAllRead={unmarkAllRead}
                    showAllChapters={showAllChapters}
                    onToggleShowAll={() => setShowAllChapters(!showAllChapters)}
                    hasMoreChapters={displayChapters.length > INITIAL_VISIBLE_CHAPTERS}
                    onDownloadAll={handleDownloadAll}
                    isDownloadingAll={isDownloadingAll}
                    chapterSelectMode={chapterSelectMode}
                    selectedChapterIds={selectedChapterIds}
                    onToggleChapterSelectMode={() => {
                        setChapterSelectMode((m) => !m);
                        setSelectedChapterIds(new Set());
                    }}
                    onToggleChapterSelected={toggleChapterSelected}
                    onDownloadSelected={handleDownloadSelected}
                    multiDlWorking={multiDlWorking}
                    onSelectAll={() =>
                        setSelectedChapterIds(
                            new Set(chapters.map((c: any) => Number(c.cap_id ?? c.id)))
                        )
                    }
                    onDeselectAll={() => setSelectedChapterIds(new Set())}
                />
            </div>

            {/* Engagement & Comments */}
            <section ref={commentsSectionRef as any} className="max-w-7xl mx-auto px-4 md:px-8 pb-32">
                <ChapterComments
                    obraId={parseInt(obraId)}
                    onCountUpdate={setCommentsCount}
                    highlightCommentId={highlightCommentId}
                />
            </section>

            {/* Float Buttons - Mobile (Subir/Descer) */}
            <div className={`fixed bottom-24 right-4 z-40 md:hidden flex flex-col gap-3 transition-all duration-300 transform ${scrollingUp ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full border border-white/20 flex items-center justify-center text-white"
                >
                    <ChevronUp size={24} />
                </button>
                <button
                    onClick={() => {
                        const el = chaptersSectionRef.current;
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-12 h-12 bg-orange-600/90 backdrop-blur-md rounded-full border border-orange-500/30 flex items-center justify-center text-white shadow-lg shadow-orange-600/30"
                >
                    <ListFilter size={24} />
                </button>
            </div>

            {/* Modal: adicionar/remover obra das listas */}
            {showListModal && (
                <div
                    className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="obra-listas-modal-title"
                >
                    <button
                        type="button"
                        className="absolute inset-0 bg-black/85 backdrop-blur-sm"
                        aria-label="Fechar"
                        onClick={() => setShowListModal(false)}
                    />
                    <div className="relative flex h-[min(88dvh,640px)] max-h-[92dvh] w-full max-w-md min-h-0 flex-col overflow-hidden rounded-t-[1.35rem] border border-white/10 border-b-0 bg-neutral-950 shadow-[0_-24px_80px_-20px_rgba(0,0,0,0.9)] animate-in fade-in slide-in-from-bottom-3 duration-200 sm:h-auto sm:max-h-[min(85dvh,640px)] sm:rounded-2xl sm:border-b sm:shadow-2xl sm:duration-300">
                        <div className="h-1 w-full shrink-0 bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500/80" />
                        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-white/5 px-5 pb-4 pt-4">
                            <div className="flex min-w-0 gap-3">
                                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                                    <ListPlus className="size-5 text-orange-500/90" aria-hidden />
                                </div>
                                <div className="min-w-0 pt-0.5">
                                    <h3 id="obra-listas-modal-title" className="text-lg font-bold tracking-tight text-white">
                                        Minhas listas
                                    </h3>
                                    {listasComObra.length > 0 ? (
                                        <p className="mt-1 text-xs leading-relaxed text-orange-300/90">
                                            Esta obra está em {listasComObra.length}{' '}
                                            {listasComObra.length === 1 ? 'lista' : 'listas'}. Toque para alternar.
                                        </p>
                                    ) : (
                                        <p className="mt-1 text-xs leading-relaxed text-white/45">
                                            Toque numa lista para adicionar esta obra. Toque de novo para remover.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowListModal(false)}
                                className="shrink-0 rounded-lg p-2 text-white/40 transition-colors hover:bg-white/5 hover:text-white"
                                aria-label="Fechar"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2 sm:px-2">
                            {isLoadingListas ? (
                                <div className="flex flex-col items-center justify-center gap-3 py-14">
                                    <Loader2 className="size-9 text-orange-500 animate-spin" aria-hidden />
                                    <p className="text-xs font-medium uppercase tracking-wider text-white/35">A carregar as suas listas…</p>
                                </div>
                            ) : userListas.length === 0 ? (
                                <div className="px-4 py-12 text-center">
                                    <p className="mb-2 text-sm font-medium text-white/55">Ainda não tem listas personalizadas</p>
                                    <p className="mb-6 text-xs leading-relaxed text-white/35">
                                        Crie uma na sua coleção ou explore listas públicas da comunidade.
                                    </p>
                                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                                        <Link
                                            to="/colecao?aba=listas"
                                            onClick={() => setShowListModal(false)}
                                            className="inline-flex items-center justify-center rounded-xl bg-orange-600 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-500"
                                        >
                                            Ir para Coleção — Listas
                                        </Link>
                                        <Link
                                            to="/listas"
                                            onClick={() => setShowListModal(false)}
                                            className="inline-flex items-center justify-center rounded-xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/70 transition-colors hover:border-white/25 hover:bg-white/[0.04] hover:text-white"
                                        >
                                            Descobrir listas
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <ul className="space-y-1.5 pb-2" role="list">
                                    {userListas.map((lista: any) => {
                                        const naLista = listaContemObra(lista);
                                        return (
                                            <li key={lista.id}>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleAddRemoveFromLista(
                                                            lista.id,
                                                            String(lista.nome ?? 'Lista'),
                                                            naLista,
                                                        )
                                                    }
                                                    className={`group flex w-full items-center gap-3 rounded-xl border p-3.5 text-left transition-all sm:gap-4 sm:p-4 ${
                                                        naLista
                                                            ? 'border-orange-500/35 bg-orange-500/[0.08] shadow-[inset_0_0_0_1px_rgba(249,115,22,0.12)]'
                                                            : 'border-white/[0.06] bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.05]'
                                                    }`}
                                                >
                                                    <div className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-white/10 ring-1 ring-white/10">
                                                        {lista.capa ? (
                                                            <ObraCoverImage
                                                                src={getObraImageUrl(lista.capa_obra_id, lista.capa, 200) || ''}
                                                                alt=""
                                                                className="size-full object-cover"
                                                                iconClassName="size-8 text-white/25"
                                                            />
                                                        ) : (
                                                            <div className="flex size-full items-center justify-center">
                                                                <ListPlus className="size-5 text-white/20" aria-hidden />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <span
                                                            className={`block truncate font-bold transition-colors ${naLista ? 'text-orange-100' : 'text-white group-hover:text-orange-400'}`}
                                                        >
                                                            {lista.nome}
                                                        </span>
                                                        <span className="mt-0.5 block text-xs text-white/40">
                                                            {lista.total_obras ?? 0} {(lista.total_obras ?? 0) === 1 ? 'obra' : 'obras'}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={`flex size-7 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                                                            naLista
                                                                ? 'border-orange-400 bg-orange-500 text-white shadow-md shadow-orange-900/40'
                                                                : 'border-white/15 bg-black/20 text-transparent group-hover:border-white/25'
                                                        }`}
                                                        aria-hidden
                                                    >
                                                        {naLista ? <Check className="size-3.5" strokeWidth={3} /> : null}
                                                    </div>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                        {userListas.length > 0 ? (
                            <div className="shrink-0 border-t border-white/5 bg-neutral-950/95 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:pb-3">
                                <Link
                                    to="/colecao?aba=listas"
                                    onClick={() => setShowListModal(false)}
                                    className="block text-center text-xs font-semibold uppercase tracking-wider text-white/40 transition-colors hover:text-orange-400/90"
                                >
                                    Gerir listas na coleção
                                </Link>
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Discord Link Modal */}
            {showLinkDiscordModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowLinkDiscordModal(false)} />
                    <div className="relative w-full max-w-sm bg-neutral-950 border border-white/10 rounded-2xl p-8 flex flex-col items-center text-center gap-6 animate-in fade-in zoom-in duration-200">
                        <DiscordIcon size={48} className="text-[#5865F2]" />
                        <div className="space-y-2">
                            <h3 className="text-xl font-bold text-white">Vincular Discord</h3>
                            <p className="text-white/60 text-sm">Receba avisos de novos capítulos diretamente no seu privado ou servidor do Discord.</p>
                        </div>
                        {linkError && <p className="text-red-500 text-xs bg-red-500/10 p-3 rounded-lg w-full">{linkError}</p>}
                        <button
                            onClick={handleVincularDiscordOAuth}
                            disabled={linkLoading}
                            className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                        >
                            {linkLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <DiscordIcon size={20} className="text-white" />}
                            <span>Vincular agora</span>
                        </button>
                    </div>
                </div>
            )}

            {/* WhatsApp Link Modal */}
            {showLinkWhatsAppModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowLinkWhatsAppModal(false)} />
                    <div className="relative w-full max-w-sm bg-neutral-950 border border-white/10 rounded-2xl p-8 flex flex-col items-center text-center gap-6 animate-in fade-in zoom-in duration-200">
                        <WhatsAppIcon size={48} className="text-[#25D366]" />
                        <div className="space-y-2">
                            <h3 className="text-xl font-bold text-white">Vincular WhatsApp</h3>
                            {!linkWhatsAppData ? (
                                <p className="text-white/60 text-sm">Receba avisos de novos capítulos no seu WhatsApp.</p>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-white/60 text-sm">Envie a mensagem abaixo para o nosso bot para confirmar seu número.</p>
                                    <div className="p-4 bg-white/5 rounded-xl border border-dashed border-white/20 select-all font-mono text-orange-500 text-lg font-bold">
                                        {linkWhatsAppData.codigo}
                                    </div>
                                    <div className="flex flex-col items-center gap-2">
                                        <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                                        <span className="text-xs text-white/40 uppercase tracking-widest animate-pulse font-bold">Aguardando confirmação...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        {linkWhatsAppError && <p className="text-red-500 text-xs bg-red-500/10 p-3 rounded-lg w-full">{linkWhatsAppError}</p>}
                        <button
                            onClick={handleLinkWhatsAppGenerate}
                            disabled={linkWhatsAppLoading}
                            className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                        >
                            {linkWhatsAppLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <WhatsAppIcon size={20} className="text-white" />}
                            <span>Vincular agora</span>
                        </button>
                        <button onClick={() => setShowLinkWhatsAppModal(false)} className="text-white/40 text-xs hover:text-white uppercase tracking-widest font-bold">Fechar</button>
                    </div>
                </div>
            )}
        </div>
    );
}
